import { LitElement, html } from 'lit';
import { getAppOverlayHost } from './app-overlay-host.js';
import { t } from './i18n.js';
import type { MapAnalyticsTrack } from '../shared/analytics-schema.js';
import { MapSaveDialogError } from './map-save-result.js';

type MapVisibility = 'public' | 'private';

class MapSaveDialogElement extends LitElement {
  static properties = {
    pro: { type: Boolean },
    visibility: { state: true },
  };

  declare pro: boolean;
  declare visibility: MapVisibility;
  result: MapVisibility | null = null;

  constructor() {
    super();
    this.pro = false;
    this.visibility = 'public';
  }

  createRenderRoot() { return this; }

  private close() {
    this.querySelector('dialog')?.close();
  }

  private save(event: SubmitEvent) {
    event.preventDefault();
    this.result = this.visibility;
    this.close();
  }

  private option(value: MapVisibility, label: string, description: string) {
    return html`
      <label class="save-visibility-option tw:focus-within:[outline:2px_solid_#15803d] tw:focus-within:outline-offset-[3px] tw:flex tw:items-start tw:gap-[16px] tw:p-[18px] tw:[border:1px_solid_var(--border-color,#cbd5e1)] tw:rounded-[10px] tw:cursor-pointer tw:[&+.save-visibility-option]:mt-[10px] tw:[&.is-selected]:border-[#15803d] tw:[&.is-selected]:[box-shadow:inset_0_0_0_1px_#15803d] tw:[&.is-selected]:[background:rgb(21_128_61_/_7%)] tw:[@media(max-width:480px)]:p-[14px] tw:[@media(max-width:480px)]:gap-[12px] ${this.visibility === value ? 'is-selected' : ''}">
        <input class="tw:w-[22px] tw:h-[22px] tw:m-[2px_0_0] tw:shrink-0 tw:accent-[#15803d] tw:rounded-[50%] tw:cursor-pointer tw:[outline:none]" type="radio" name="map-visibility" value=${value} .checked=${this.visibility === value} ?autofocus=${this.visibility === value}
          @change=${() => { this.visibility = value; }}>
        <span><strong class="tw:block tw:text-[16px] tw:leading-[1.5]">${label}</strong><span class="save-visibility-description tw:block tw:mt-[4px] tw:[color:var(--text-secondary,#475569)] tw:text-[14px] tw:leading-[1.5]">${description}</span></span>
      </label>`;
  }

  render() {
    return html`
      <dialog class="tw:box-border tw:w-[calc(100%-32px)] tw:max-w-[520px] tw:max-h-[calc(100vh-32px)] tw:supports-[height:100dvh]:max-h-[calc(100dvh-32px)] tw:m-auto tw:p-[28px] tw:[border:0] tw:rounded-[20px] tw:[background:var(--bg-toolbar,#fff)] tw:[color:var(--text-primary,#0f172a)] tw:[box-shadow:0_24px_80px_rgb(0_0_0_/_25%)] tw:backdrop:[background:rgb(0_0_0_/_50%)] tw:backdrop:[backdrop-filter:blur(4px)] tw:[@media(max-width:480px)]:p-[20px]" aria-labelledby="save-visibility-title" @click=${(event: MouseEvent) => {
        if (event.target === event.currentTarget) this.close();
      }}>
        <form @submit=${this.save}>
          <div class="save-visibility-header tw:flex tw:items-center tw:justify-between tw:gap-[16px] tw:mb-[20px]">
            <h2 id="save-visibility-title" class="tw:[color:var(--text-primary,#0f172a)] tw:m-0 tw:text-[26px] tw:font-bold tw:leading-[1.25] tw:[@media(max-width:480px)]:text-[24px]">${t('save.dialog_title')}</h2>
            <button type="button" class="save-visibility-close tw:min-h-[44px] tw:[border:0] tw:rounded-[8px] tw:font-inherit tw:cursor-pointer tw:grid tw:shrink-0 tw:place-items-center tw:w-[44px] tw:p-0 tw:[background:transparent] tw:[color:var(--text-secondary,#475569)] tw:focus:[outline:2px_solid_var(--text-primary,#0f172a)] tw:focus:outline-offset-[3px] tw:[@media(hover:hover)_and_(pointer:fine)]:hover:[background:var(--bg-hover,#f1f5f9)]" aria-label=${t('confirm_dialog.close')} @click=${this.close}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          ${this.pro ? html`
            <fieldset class="tw:min-w-0 tw:p-0 tw:m-0 tw:[border:0]">
              <legend class="tw:p-0 tw:m-[0_0_16px] tw:text-[16px] tw:leading-[1.5] tw:[color:var(--text-secondary,#475569)]">${t('save.visibility_title')}</legend>
              ${this.option('private', t('save.private_option'), t('save.private_description'))}
              ${this.option('public', t('save.public_option'), t('save.public_description'))}
            </fieldset>` : html`
            <p class="tw:p-0 tw:m-[0_0_16px] tw:text-[16px] tw:leading-[1.5] tw:[color:var(--text-secondary,#475569)]">${t('save.public_description')}</p>
            <p class="save-visibility-description tw:block tw:p-0 tw:m-[4px_0_16px] tw:[color:var(--text-secondary,#475569)] tw:text-[14px] tw:leading-[1.5]">${t('save.private_requires_pro')}</p>`}
          <div class="save-visibility-actions tw:flex tw:justify-end tw:gap-[12px] tw:mt-[24px]">
            <button type="button" class="save-visibility-cancel tw:min-h-[44px] tw:[border:0] tw:rounded-[8px] tw:font-inherit tw:cursor-pointer tw:p-[10px_14px] tw:[background:transparent] tw:[color:var(--text-secondary,#475569)] tw:focus:[outline:2px_solid_var(--text-primary,#0f172a)] tw:focus:outline-offset-[3px] tw:[@media(hover:hover)_and_(pointer:fine)]:hover:[background:var(--bg-hover,#f1f5f9)]" @click=${this.close}>${t('confirm.cancel')}</button>
            <button type="submit" class="save-visibility-submit tw:min-h-[44px] tw:[border:0] tw:rounded-[8px] tw:font-inherit tw:cursor-pointer tw:p-[10px_20px] tw:[background:#15803d] tw:text-white tw:font-semibold tw:focus:[outline:2px_solid_var(--text-primary,#0f172a)] tw:focus:outline-offset-[3px] tw:[@media(hover:hover)_and_(pointer:fine)]:hover:[background:#166534]" ?autofocus=${!this.pro}>${t('save.dialog_title')}</button>
          </div>
        </form>
      </dialog>`;
  }
}

customElements.define('map-save-dialog-element', MapSaveDialogElement);

export async function chooseMapVisibility(pro: boolean, track?: MapAnalyticsTrack): Promise<MapVisibility | null> {
  if (!pro) return 'public';
  const trigger = document.activeElement;
  const element = new MapSaveDialogElement();
  element.pro = pro;
  element.visibility = pro ? 'private' : 'public';
  getAppOverlayHost().appendChild(element);
  try {
    await element.updateComplete;
    const dialog = element.querySelector('dialog');
    if (!dialog) throw new MapSaveDialogError();
    const closed = new Promise<void>(resolve => dialog.addEventListener('close', () => resolve(), { once: true }));
    await Promise.resolve(dialog.showModal());
    track?.('save_dialog_opened');
    await closed;
    track?.(element.result === null ? 'save_dialog_canceled' : 'save_dialog_submitted');
    return element.result;
  } catch (cause) {
    track?.('save_dialog_failed');
    throw new MapSaveDialogError(cause);
  } finally {
    element.remove();
    if (trigger instanceof HTMLElement) trigger.focus();
  }
}
